import React, { useMemo, useState } from 'react';
import { Form, InputNumber, Typography, Tooltip, Spin, Alert } from 'antd';
import type { FormInstance } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { UNIT_CREDIT_PRICE, currencyOptions } from 'src/constants/unitPrice';

const { Title, Text } = Typography;

// Core types
type ZoneKey = string;

interface Zone {
	id: string;
	name: string;
	description: string;
	basePrice: number;
	formattedPrice: string;
	countryCount: number;
}

interface EnabledCountry {
	zone: string;
	name: string;
	code: string;
	basePrice: number;
}

interface SMSPricingConfigProps {
	form: FormInstance;
	data: {
		zones: Zone[];
		enabledCountries: EnabledCountry[];
	};
	isLoading: boolean;
	error: Error | null;
	currency: string;
	exchangeRates?: Record<string, number>;
}

const SMSPricingConfig: React.FC<SMSPricingConfigProps> = ({
	form,
	data,
	isLoading,
	error,
	currency,
	exchangeRates = {},
}) => {
	// Track if initial values have been set
	const [initialValuesSet, setInitialValuesSet] = useState(false);

	const zones = data?.zones || [];
	const enabledCountries = data?.enabledCountries || [];

	// Function to convert price to USD (same as in ResellerPlansCreation)
	const convertToUSD = (price: number, fromCurrency: string): number => {
		if (fromCurrency === 'usd') {
			return price;
		}
		const rate = exchangeRates[fromCurrency.toUpperCase()];
		return rate ? price / rate : price;
	};

	// Get currency symbol
	const getCurrencySymbol = () => {
		return currencyOptions.find((opt) => opt.value === currency)?.symbol || '$';
	};

	// Filter zones to only show enabled ones
	const enabledZones = useMemo(() => {
		if (!zones.length || !enabledCountries.length) {
			return [];
		}

		// Get a set of zone IDs that have at least one enabled country
		const enabledZoneIds = new Set(enabledCountries.map((country: EnabledCountry) => country.zone));

		// Filter zones to only include those with enabled countries
		return zones.filter((zone: Zone) => enabledZoneIds.has(zone.id));
	}, [zones, enabledCountries]);

	const calculateMargin = (chargePrice: number, basePrice: number) => {
		if (chargePrice === 0) {
			return { text: '(Free)', margin: 0 };
		}

		// Convert charge price to USD for accurate margin calculation
		const chargePriceInUSD = convertToUSD(chargePrice, currency);
		const marginDollars = chargePriceInUSD - basePrice;

		return {
			text:
				marginDollars >= 0
					? `(+$${marginDollars.toFixed(4)}/SMS profit)`
					: `(-$${Math.abs(marginDollars).toFixed(4)}/SMS loss)`,
			margin: marginDollars,
		};
	};

	// Handle individual field updates
	const updateFieldValue = (zone: ZoneKey, value: number | null | string) => {
		// Convert empty, null, or undefined values to 0
		let safeValue = 0;

		if (value !== null && value !== undefined && value !== '') {
			// Convert to number and ensure it's valid
			const numValue = Number(value);
			safeValue = isNaN(numValue) ? 0 : numValue;
		}

		// Get current form values
		const currentValues = form.getFieldValue('smsZonePrices') || {};

		// Update form with all values intact except the changed one
		form.setFieldsValue({
			smsZonePrices: {
				...currentValues,
				[zone]: safeValue,
			},
		});
	};

	// Initialize form values when zones data is loaded - but only once
	React.useEffect(() => {
		if (enabledZones.length > 0 && !initialValuesSet) {
			const initialValues = enabledZones.reduce((acc: Record<string, number>, zone: Zone) => {
				acc[zone.id] = zone.basePrice;
				return acc;
			}, {});

			form.setFieldsValue({
				smsZonePrices: initialValues,
			});

			setInitialValuesSet(true);
		}
	}, [enabledZones, form, initialValuesSet]);

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-10">
				<Spin size="large" />
				<Text className="mt-3">Loading SMS pricing information...</Text>
			</div>
		);
	}

	if (error) {
		return (
			<Alert
				message="Error Loading Pricing"
				description="There was an error loading the SMS pricing information. Please try again later."
				type="error"
				showIcon
			/>
		);
	}

	if (enabledZones.length === 0) {
		return (
			<Alert
				message="No SMS Zones Available"
				description="There are currently no enabled SMS zones available for pricing configuration."
				type="info"
				showIcon
			/>
		);
	}

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<div className="flex items-center gap-2">
					<Title level={5}>Set SMS Pricing by Zone ({currency.toUpperCase()})</Title>
					<Tooltip
						title={`Enter the price you want to charge per SMS message in ${currency.toUpperCase()}. The base cost is shown for reference.`}
					>
						<InfoCircleOutlined className="text-blue-500" />
					</Tooltip>
				</div>
				<Text type="secondary">
					Set your pricing for SMS messages by zone. Enter the price per SMS in{' '}
					{currency.toUpperCase()}. The base cost we charge you is shown for reference. The
					difference is your margin. Empty values will be treated as {getCurrencySymbol()}0.
				</Text>

				<Form.Item name="smsZonePrices" hidden>
					<input type="hidden" />
				</Form.Item>

				<div className="space-y-4">
					{enabledZones.map((zone: Zone) => (
						<div key={zone.id} className="border-b pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0">
							<div>
								<Text strong>{zone.name}</Text>
								<Text type="secondary" className="block text-xs">
									{zone.description}
								</Text>
								<Text type="secondary" className="text-xs mt-1 block">
									Price You Pay: {zone.formattedPrice}
								</Text>
							</div>
							<div className="flex items-center gap-4 mt-2">
								<div className="flex-1 relative">
									<Form.Item noStyle shouldUpdate>
										{() => {
											const allValues = form.getFieldValue('smsZonePrices') || {};
											const value = allValues[zone.id];
											// Ensure value is displayed as 0 if it's null, undefined, or empty
											const displayValue =
												value === null || value === undefined || value === '' ? 0 : value;

											return (
												<div className="w-full">
													<Tooltip title="Each credit costs approximately 12 cents after the free monthly credits are exhausted. Additional credits will incur charges at this rate.">
														<InputNumber
															className="w-full"
															min={0}
															max={1}
															step={0.001}
															precision={4}
															value={displayValue}
															prefix={getCurrencySymbol()}
															addonAfter={`per SMS (${currency.toUpperCase()})`}
															onChange={(value) => updateFieldValue(zone.id, value)}
															placeholder={`Enter price in ${currency.toUpperCase()}`}
															stringMode={false}
															controls={true}
															onBlur={() => {
																// Ensure value is 0 on blur if it's empty, null, or undefined
																const currentValue = form.getFieldValue('smsZonePrices')?.[zone.id];
																if (
																	currentValue === null ||
																	currentValue === undefined ||
																	currentValue === ''
																) {
																	updateFieldValue(zone.id, 0);
																}
															}}
														/>
													</Tooltip>

													{/* In Credit Terms: {Math.round(dollarValue / UNIT_CREDIT_PRICE)} */}

													<Text type="secondary" className="text-xs mt-1 block">
														credits: {(displayValue / UNIT_CREDIT_PRICE).toFixed(2)}
													</Text>
												</div>
											);
										}}
									</Form.Item>
								</div>
								<div className="w-48 text-right">
									<Form.Item noStyle shouldUpdate>
										{() => {
											const allValues = form.getFieldValue('smsZonePrices') || {};
											const value = allValues[zone.id] || 0;

											return (
												<div>
													<Text strong>
														{value === 0
															? 'Free'
															: `${getCurrencySymbol()}${Number(value).toFixed(4)}`}
													</Text>
													<Text
														className="ml-2"
														type={value === 0 ? 'secondary' : undefined}
														style={{
															color:
																value === 0
																	? undefined
																	: calculateMargin(Number(value), zone.basePrice).margin > 0
																		? '#52c41a'
																		: '#ff4d4f',
														}}
													>
														{value === 0
															? '(No charge)'
															: calculateMargin(Number(value), zone.basePrice).text}
													</Text>
												</div>
											);
										}}
									</Form.Item>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default SMSPricingConfig;
